import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/api";
import { Shield, Video, CalendarCheck, Search, ArrowRight, Star, CalendarDays } from "lucide-react";
import { CoachCard } from "@/components/CoachCard";

const HERO = "https://images.unsplash.com/photo-1516226415502-d6624544376b?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

export default function Landing() {
  const [coaches, setCoaches] = useState([]);

  useEffect(() => {
    api.get("/coaches").then((r) => setCoaches(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      <section className="relative grain overflow-hidden border-b border-white/10">
        <img src={HERO} alt="Hockey goalie" className="absolute inset-0 w-full h-full object-cover max-h-[80vh] opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 rise">
            <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#00e5ff] border-l-2 border-[#00e5ff] pl-3 mb-8">
              Goalie-only coaching marketplace
            </span>
            <h1 className="display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter font-bold leading-[0.92]">
              Stop more pucks.<br />
              <span className="text-[#00e5ff]">Book a real</span> goalie coach.
            </h1>
            <p className="text-base text-zinc-300 leading-relaxed mt-8 max-w-xl">
              Search vetted goaltending coaches plus the off-ice team around them — strength, mobility, vision
              and mental performance. Compare profiles, see live availability, book and pay in one place — or
              send game tape for a remote breakdown.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link to="/coaches" data-testid="hero-find-coaches-btn"
                className="group bg-[#00e5ff] text-black font-bold uppercase tracking-wide px-8 py-4 flex items-center gap-2 hover:bg-[#00b3cc] transition-colors duration-200">
                Find a coach <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link to="/register" data-testid="hero-coach-signup-btn"
                className="border border-white/25 text-white uppercase tracking-wide px-8 py-4 hover:border-[#00e5ff] hover:text-[#00e5ff] transition-colors duration-200">
                Coach with us
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 lg:pt-16 grid grid-cols-2 gap-4">
            {[
              { k: "5", v: "Training disciplines" },
              { k: "4.9", v: "Average coach rating" },
              { k: "48h", v: "Video analysis turnaround" },
              { k: "$49", v: "Remote breakdown from" },
            ].map((s, i) => (
              <div key={s.v} className="bg-black/60 backdrop-blur-xl border border-white/10 p-6 rise" style={{ animationDelay: `${0.1 + i * 0.08}s` }}>
                <div className="display text-4xl font-bold text-[#00e5ff] leading-none">{s.k}</div>
                <div className="text-xs text-zinc-400 mt-2 uppercase tracking-wide">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {[
              { icon: Search, t: "Search & compare", d: "Filter by discipline, city, level, experience and price. Put two coaches side by side before you commit." },
              { icon: CalendarCheck, t: "Book real availability", d: "On-ice coaches, strength, mobility, vision and mental performance — pick a slot, pay by card, done." },
              { icon: Video, t: "Remote video analysis", d: "No coach nearby? Send a game link and get a frame-by-frame written breakdown." },
              { icon: CalendarDays, t: "Multi-day camps", d: "Book a whole goalie camp in one purchase — ice, off-ice and video included." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="border-r border-b border-white/10 p-8 lg:p-12 group hover:bg-[#121212] transition-colors duration-200">
              <Icon className="w-7 h-7 text-[#00e5ff] mb-6" strokeWidth={2} />
              <h3 className="display text-xl uppercase tracking-tight font-bold">{t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mt-3">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#ff3b30]">Top rated</span>
            <h2 className="display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight font-bold mt-2">Featured coaches</h2>
          </div>
          <Link to="/coaches" data-testid="see-all-coaches-link" className="text-xs uppercase tracking-wide text-[#00e5ff] hover:underline">See all</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coaches.map((c) => <CoachCard key={c.id} coach={c} selected={false} onCompare={() => {}} />)}
        </div>
      </section>
    </div>
  );
}
